import type { ModuleItem } from '../../model/pages';
import type { SectionKit } from './kit';
import { normalizeSectionDesign } from '../../model/section-design';
import { sectionGridProps } from '../section-design';

/** Curriculum: numbered modules, each with a title, a line of detail and an
 *  optional lesson list. Default = stacked rows (a large index on the left, the
 *  content on the right). `variant` 'grid' = 2-up cards. An optional module
 *  image shows as a thumbnail. */
export default function ModulesBlock({ k }: { k: SectionKit }) {
  const { section, s, brand, tz, txt, withHead, ph } = k;
  const all = section.modules ?? [];
  const mods = all.map((m, i) => ({ m, i })).filter(({ m }) => m.title || m.body);
  if (!mods.length) return withHead(ph('Modules', 'Add modules in the panel'));
  const patch = (i: number, p: Partial<ModuleItem>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { modules: a }; };
  const design = normalizeSectionDesign(section.design);
  const grid = section.variant === 'grid';
  const gridProps = sectionGridProps(design, k.stack, grid ? undefined : 'module-rows');
  const lessonsOf = (m: ModuleItem, i: number) => {
    const ls = (m.lessons ?? []).map((l, j) => ({ l, j })).filter(({ l }) => Boolean(l));
    if (!ls.length) return null;
    return (
      <ul className={`mt-4 grid gap-2 ${grid ? '' : 'sm:grid-cols-2 sm:gap-x-6'}`}>
        {ls.map(({ l, j }) => (
          <li key={j} className={`flex items-start gap-2.5 ${tz('small')}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="mt-[3px] flex-none" aria-hidden><path d="M20 6 9 17l-5-5" /></svg>
            <span>{txt(l, (t) => { const ls2 = [...(m.lessons ?? [])]; ls2[j] = t; return patch(i, { lessons: ls2 }); })}</span>
          </li>
        ))}
      </ul>
    );
  };
  return withHead(
    <div className={`mx-auto grid gap-4 ${grid ? 'max-w-5xl sm:grid-cols-2 sm:gap-5' : 'max-w-4xl'}`} {...gridProps}>
      {mods.map(({ m, i }, n) => (
        <article key={i} className={`${s.card} ${grid ? 'flex flex-col p-6' : 'grid gap-4 p-5 sm:grid-cols-[88px_1fr] sm:gap-6 sm:p-7'}`}>
          {grid ? (
            m.image
              ? <img src={m.image} alt="" loading="lazy" decoding="async" className="-mx-1 -mt-1 mb-5 aspect-[16/9] w-[calc(100%+0.5rem)] rounded-xl object-cover" />
              : null
          ) : (
            <div className="flex items-center gap-3 sm:block">
              {m.image
                ? <img src={m.image} alt="" loading="lazy" decoding="async" className="h-16 w-16 rounded-xl object-cover sm:h-[88px] sm:w-[88px]" />
                : <span className="text-4xl font-extrabold leading-none tabular-nums tracking-tight sm:text-5xl" style={{ color: brand }}>{String(n + 1).padStart(2, '0')}</span>}
              {m.label && <span className={`text-xs font-semibold sm:hidden ${s.label ?? s.sub}`}>{txt(m.label, (t) => patch(i, { label: t }))}</span>}
            </div>
          )}
          <div className="min-w-0">
            {m.label && <div className={`${grid ? '' : 'hidden sm:block'} mb-1.5 text-xs font-semibold ${s.label ?? s.sub}`} style={grid ? { color: brand } : undefined}>{txt(m.label, (t) => patch(i, { label: t }))}</div>}
            {m.title && <h3 className={`${tz('title')} text-lg sm:text-xl ${s.heading}`}>{txt(m.title, (t) => patch(i, { title: t }))}</h3>}
            {m.body && <p className={`mt-1.5 ${tz('small')} ${s.sub} whitespace-pre-line`}>{txt(m.body, (t) => patch(i, { body: t }), true)}</p>}
            {lessonsOf(m, i)}
          </div>
        </article>
      ))}
    </div>,
  );
}
